/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { CurrencyValue } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { convertToSentenceCase } from 'ml-portals-util-js';
import MLPAUMBCoveragesComponent from '../MLPAUMBCoveragesComponent/MLPAUMBCoveragesComponent';
import MLEditableCoveragesComponent from '../MLEditableCoveragesComponent/MLEditableCoveragesComponent';
import MLReadOnlyCoverageComponent from '../MLReadOnlyCoverageComponent/MLReadOnlyCoverageComponent';
import '@xengage/gw-platform-translations';
import metadata from './MLPAEditCoveragesComponent.metadata.json5';
import styles from './MLPAEditCoveragesComponent.module.scss';
import messages from './MLPAEditCoveragesComponent.messages';

function MLPAEditCoveragesComponent(props) {
    const {
        model,
        loading,
        onCovChange,
        onGeneralChange,
        isDisplayOnly,
        addVehicle,
        editVehicle,
        removeVehicle,
        editCoverage,
        hideEditLinks,
        hideCoverageButton,
        selectedTermType,
        submissionVM
    } = props;

    const translator = useTranslator();

    const isQb = appConfig.portalCode === 'QB';

    const isNamedNonOwner = useCallback(() => {
        return model?.vehicleCoverages?.some((vehicle) => {
            return vehicle.vehicleName === 'Named Non-Owner';
        });
    }, [model]);

    const TERM_TYPE_HALF_YEAR = 'HalfYear';
    const TERM_TYPE_7_DAY = '7Days';

    const getTermtypeLabel = useCallback(() => {
        let label = translator(messages.termTypeAnnual);
        if (selectedTermType === TERM_TYPE_HALF_YEAR) {
            label = translator(messages.termType6Month);
        } else if (selectedTermType === TERM_TYPE_7_DAY) {
            label = translator(messages.termType7day);
        }
        return label;
    }, [translator, selectedTermType]);

    const getVehicleCostContent = useCallback((vehicle, index) => {
        return (
            <span className={styles.vehicleCostFlex}>
                <strong>
                    <CurrencyValue
                        currency={_.get(vehicle, 'vehicleMonthlyCost_ML.currency', 'usd')}
                        currencyDisplay="symbol"
                        id={`vehicleCurrencySymbol${index}`}
                        placeholder="-"
                        showFractions
                        showGrouping
                        tag="div"
                        value={_.get(vehicle, 'vehicleMonthlyCost_ML.amount', 0)}
                    />
                </strong>
            </span>
        );
    }, []);

    const hasExcludedCoverages = useCallback(() => {
        return _.get(model, 'exclusionCoverages_ML.length', 0) > 0;
    }, [model]);

    const onRemoveVehicle = useCallback((evt) => {
        if (evt.index >= 0) {
            const vehicle = _.get(model, `vehicleCoverages[${evt.index}]`, {});
            removeVehicle(vehicle);
        }
    }, [model, removeVehicle]);

    const vehicleList = () => {
        return _.get(submissionVM, 'lobData.personalAuto.coverables.vehicles.value', [])
    }

    const generateVehicleOverrides = useCallback(() => {
        const vehicleCovs = _.get(model, 'vehicleCoverages', []);
        const vehicleOverrides = vehicleCovs.map((vehicle, index) => {
            return {
                [`mlPaUmbCoverages${index}`]: {
                    coverages: model,
                    vehicle: vehicle,
                    editMode: !isDisplayOnly,
                    onCovChange: onCovChange,
                    showMonthlyPremium: isQb,
                    loading: loading,
                    onGeneralChange: onGeneralChange,
                    displayedLineCoverages: new Map()
                },
                [`nonDisplayTotalP${index}`]: {
                    content: isNamedNonOwner() ? translator(messages.coverageTotal) : translator(messages.vehicleTotal)
                },
                [`nonDisplayTermType${index}`]: {
                    content: getTermtypeLabel()
                },
                [`nonDisplaySectionHeader${index}`]: {
                    visible: hasExcludedCoverages()
                },
                [`nonDisplayEditableCovs${index}`]: {
                    visible: hasExcludedCoverages()
                },
                [`displaySectionHeader${index}`]: {
                    // visible: hasExcludedCoverages()
                    visible: false  // METINC-13398. Notice in v9 ml-pa-edit-coverages.html, a typo 'lenth > 0'
                },
                [`displayEditableCovs${index}`]: {
                    visible: hasExcludedCoverages()
                },
                [`nonDisplayModeCov${index}`]: {
                    visible: !isDisplayOnly
                },
                [`displayModeCov${index}`]: {
                    visible: isDisplayOnly
                },
                [`nonDisplayVehicleCostSpan${index}`]: {
                    content: getVehicleCostContent(vehicle, index)
                },
                [`displayVehicleCostSpan${index}`]: {
                    content: getVehicleCostContent(vehicle, index)
                },
                [`displayTotalP${index}`]: {
                    content: isNamedNonOwner() ? translator(messages.coverageTotal) : translator(messages.vehicleTotal)
                },
                [`displayTermType${index}`]: {
                    content: isNamedNonOwner() ? translator(messages.coverageTotal) : translator(messages.vehicleTotal)
                },
                [`nonDisplayEditableExclusionCovs${index}`]: {
                    visible: !_.isEmpty(_.get(model, 'exclusionCoverages_ML', [])),
                    coverages: _.get(model, 'exclusionCoverages_ML', []),
                    onCovChange: onCovChange,
                    loading: loading,
                    onGeneralChange: onGeneralChange,
                    displayHelpMessage: true,
                    allowSelectCoverage: true
                },
                [`displayEditableExclusionCovs${index}`]: {
                    // visible: !_.isEmpty(_.get(model, 'exclusionCoverages_ML', [])),
                    visible: false, // METINC-13398
                    coverages: _.get(model, 'exclusionCoverages_ML', []),
                    onCovChange: onCovChange,
                    loading: loading,
                    onGeneralChange: onGeneralChange,
                    displayHelpMessage: true,
                    showEditCoverage: true,
                    editCoverage: editCoverage
                },
                [`hrBorder${index}`]: {
                    visible: isQb
                },
                [`coverageButton${index}`]: {
                    visible: (!hideCoverageButton && isQb && index !== 0) ? false : !hideCoverageButton
                },
                [`summaryEditCoverage${index}`]: {
                    content: (isQb && index === 0) ? translator(messages.pickYourCoverageLevel) : translator(messages.editCoverage),
                    visible: !isQb
                },
                [`summaryHeaderMgmtLinks${index}`]: {
                    visible: !hideEditLinks && !isNamedNonOwner()
                },
                [`summaryHeaderRemoveVehicle${index}`]: {
                    visible: isQb ? vehicleList.length > 1 : true
                },
                [`summaryHeaderDivider${index}`]: {
                    visible: isQb ? vehicleList.length > 1 : true
                },
                [`notNamedNonOwnerText${index}`]: {
                    className: isQb ? "summary-header__count-text text-primary-color" : "summary-header__count-text"
                }
            };
        });

        return Object.assign({}, ...vehicleOverrides);
    }, [model, isDisplayOnly, onCovChange, isQb, loading, onGeneralChange, isNamedNonOwner, translator, getTermtypeLabel, hasExcludedCoverages, getVehicleCostContent, editCoverage]);

    const overrides = {
        vehicleCovHeader: {
            visible: false
        },
        editCovsDivIterable: {
            data: _.get(model, 'vehicleCoverages', [])
        },
        yourCovHeader: {
            content: isNamedNonOwner() ? translator(messages.yourCoverage) : translator(messages.yourVehicleCoverage)
        },
        divHideEditLinks: {
            visible: !hideEditLinks
        },
        addVehicleButton: {
            className: isQb ? "secondaryButton w-auto" : "",
            content: isQb ? convertToSentenceCase(translator(messages.addVehicle)) : translator(messages.addVehicle)
        },
        ...generateVehicleOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddVehicle: addVehicle,
            onEditVehicle: editVehicle,
            onRemoveVehicle,
            onEditCoverage: editCoverage
        },
        resolveComponentMap: {
            mlpaumbcoveragescomponent: MLPAUMBCoveragesComponent,
            mleditablecoveragescomponent: MLEditableCoveragesComponent,
            mlreadonlycoveragecomponent: MLReadOnlyCoverageComponent
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLPAEditCoveragesComponent.propTypes = {
    model: PropTypes.shape({}),
    onCovChange: PropTypes.func,
    loading: PropTypes.bool,
    onGeneralChange: PropTypes.func,
    isDisplayOnly: PropTypes.bool,
    addVehicle: PropTypes.func,
    editVehicle: PropTypes.func,
    removeVehicle: PropTypes.func,
    editCoverage: PropTypes.func,
    hideEditLinks: PropTypes.bool,
    hideCoverageButton: PropTypes.bool,
    selectedTermType: PropTypes.string

};

export default MLPAEditCoveragesComponent;
